import axios from "axios";
import { storage } from "./storage";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const decodeJwtPayload = (token) => {
  if (!token) return null;
  try {
    const parts = String(token).split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, "=");
    return JSON.parse(atob(padded));
  } catch (error) {
    return null;
  }
};

export const getTokenExpiryMs = (token) => {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (!exp) return null;
  return Number(exp) * 1000;
};

const persistToken = (accessToken, expiresIn) => {
  if (!accessToken) return;
  storage.set("token", accessToken);
  if (expiresIn !== undefined) {
    storage.set("expires_in", expiresIn);
  }
  const expMs = getTokenExpiryMs(accessToken);
  if (expMs) {
    storage.set("token_expires_at", expMs);
    return;
  }
  const fallback = Number(expiresIn);
  if (Number.isFinite(fallback)) {
    storage.set("token_expires_at", Date.now() + fallback * 1000);
  }
};

const clearTokenStorage = () => {
  storage.remove("token");
  storage.remove("expires_in");
  storage.remove("token_expires_at");
};

const isAuthEndpoint = (url = "") => {
  const target = String(url || "");
  return ["/login", "/refresh-token", "/verify-token", "/logout"].some((path) =>
    target.includes(path)
  );
};

let refreshPromise = null;
const requestTokenRefresh = async () => {
  const token = storage.get("token");
  if (!token) throw new Error("No hay token");
  const response = await axios.post(
    `${API_BASE_URL}/refresh-token`,
    { token },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  const { access_token, expires_in } = response.data || {};
  if (!access_token) throw new Error("Token inválido en refresh");
  persistToken(access_token, expires_in);
  return access_token;
};

// ✅ INTERCEPTOR DE REQUEST - Agregar token
api.interceptors.request.use(
  (config) => {
    const token = storage.get("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("❌ Error en request:", error);
    return Promise.reject(error);
  }
);

// ✅ INTERCEPTOR DE RESPONSE - Manejar errores
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config;

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest.url)
    ) {
      originalRequest._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = requestTokenRefresh().finally(() => {
            refreshPromise = null;
          });
        }
        const newToken = await refreshPromise;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.warn("⚠️ Token inválido, cerrando sesión");
        clearTokenStorage();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    if (status === 401) {
      console.warn("⚠️ Token inválido, cerrando sesión");
      clearTokenStorage();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// =====================================================
// 🔹 FUNCIONES DE AUTENTICACIÓN
// =====================================================

export const loginUser = async (username, password) => {
  try {
    const response = await api.post("/login", {
      username,
      password,
    });
    
    const { access_token, expires_in } = response.data;
    
    // ✅ GUARDAR TOKEN EN LOCALSTORAGE
    persistToken(access_token, expires_in);
    
    console.log("✅ Login exitoso");
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error en login:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error en login",
    };
  }
};

export const registerUser = async (payload) => {
  try {
    const response = await api.post("/register", payload);
    
    console.log("✅ Registro exitoso");
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error en registro:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error en registro",
    };
  }
};

export const forgotPassword = async (email) => {
  try {
    const response = await api.post("/forgot-password", { email });
    return { success: true, data: response.data };
  } catch (error) {
    console.error("❌ Error en forgot password:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error recuperando clave",
    };
  }
};

export const createAdminUser = async (email, password, options = {}) => {
  try {
    const response = await api.post("/admin/users", {
      email,
      password,
      symbol: options.symbol,
      trading_pairs: options.tradingPairs,
      trade_mode: options.tradeMode,
      pionex_api_key: options.pionexApiKey,
      pionex_api_secret: options.pionexApiSecret,
      telegram_chat_ids: options.telegramChatIds,
      dashboard_view: options.dashboardView,
    });
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error creando usuario:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error creando usuario",
      data: error.response?.data,
    };
  }
};

export const getAdminUsers = async () => {
  try {
    const response = await api.get("/admin/users");
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error obteniendo usuarios:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error obteniendo usuarios",
      data: error.response?.data,
    };
  }
};

export const getAdminUsersOverview = async () => {
  try {
    const response = await api.get("/admin/users/overview");
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error obteniendo overview usuarios:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error obteniendo overview",
      data: error.response?.data,
    };
  }
};

export const getAdminUserPairsIntelligence = async (username) => {
  try {
    const response = await api.get(`/admin/users/${encodeURIComponent(username)}/pairs-intelligence`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error obteniendo inteligencia de pares:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error obteniendo pares del usuario",
      data: error.response?.data,
    };
  }
};

export const applyAdminUserPairSuggestions = async (username) => {
  try {
    const response = await api.post(
      `/admin/users/${encodeURIComponent(username)}/pairs-intelligence/apply`
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error aplicando sugerencias de pares:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error aplicando sugerencias",
      data: error.response?.data,
    };
  }
};

export const getAdminPreregistros = async () => {
  try {
    const response = await api.get("/admin/preregistros");
    return { success: true, data: response.data };
  } catch (error) {
    console.error("❌ Error obteniendo preregistros:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error obteniendo preregistros",
      data: error.response?.data,
    };
  }
};

export const approvePreregistro = async (payload) => {
  try {
    const response = await api.post("/admin/preregistros/approve", payload);
    return { success: true, data: response.data };
  } catch (error) {
    console.error("❌ Error aprobando preregistro:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error aprobando preregistro",
      data: error.response?.data,
    };
  }
};

export const deletePreregistro = async (email) => {
  try {
    const response = await api.delete(`/admin/preregistros/${encodeURIComponent(email)}`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error("❌ Error eliminando preregistro:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error eliminando preregistro",
      data: error.response?.data,
    };
  }
};

export const getAdminActiveOrders = async (params = {}) => {
  try {
    const response = await api.get("/admin/orders/active", {
      params: {
        ...params,
        _ts: Date.now(),
      },
      timeout: 20000,
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error obteniendo órdenes admin:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error obteniendo órdenes admin",
      data: error.response?.data,
    };
  }
};

export const updateAdminUser = async (email, payload = {}) => {
  try {
    const response = await api.put(`/admin/users/${encodeURIComponent(email)}`, payload);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error actualizando usuario:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error actualizando usuario",
      data: error.response?.data,
    };
  }
};

export const updateCurrentUserProfile = async (payload = {}) => {
   try {
     const response = await api.put("/me/profile", payload);
     return {
       success: true,
       data: response.data,
     };
   } catch (error) {
     console.error("❌ Error actualizando perfil:", error.response?.data || error.message);
     return {
       success: false,
       error: error.response?.data?.detail || "Error actualizando perfil",
       data: error.response?.data,
     };
   }
 };

export const activateProduction = async ({ txHash = "", method = "tron", reference = "", occurredAt = "" } = {}) => {
   try {
     const response = await api.post("/me/activate", {
       tx_hash: txHash || null,
       payment_method: method,
       payment_reference: reference || null,
       payment_occurred_at: occurredAt || null,
     });
     return {
       success: true,
       data: response.data,
     };
   } catch (error) {
     console.error("❌ Error activando producción:", error.response?.data || error.message);
     return {
       success: false,
       error: error.response?.data?.detail || "Error validando el pago",
       data: error.response?.data,
     };
   }
 };

export const paySubscription = async ({ txHash = "", method = "tron", reference = "", occurredAt = "" } = {}) => {
   try {
     const response = await api.post("/me/subscription/pay", {
       tx_hash: txHash || null,
       payment_method: method,
       payment_reference: reference || null,
       payment_occurred_at: occurredAt || null,
     });
     return {
       success: true,
       data: response.data,
     };
   } catch (error) {
     console.error("❌ Error pagando suscripción:", error.response?.data || error.message);
     return {
       success: false,
       error: error.response?.data?.detail || "Error validando el pago de suscripción",
       data: error.response?.data,
     };
   }
 };

export const confirmSubscriptionPayment = async (username, action) => {
   try {
     const response = await api.post("/admin/subscription/confirm", { username, action });
     return {
       success: true,
       data: response.data,
     };
   } catch (error) {
     console.error("❌ Error confirmando pago de suscripción:", error.response?.data || error.message);
     return {
success: false,
        error: error.response?.data?.detail || "Error confirmando el pago",
        data: error.response?.data,
      };
    }
  };

export const markSubscriptionPaid = async (username, options = {}) => {
   try {
     const response = await api.post("/admin/subscription/mark-paid", {
       username,
       amount: options.amount ?? null,
       reference: options.reference || "",
       note: options.note || "",
     });
     return {
       success: true,
       data: response.data,
     };
   } catch (error) {
     console.error("❌ Error marcando período pagado:", error.response?.data || error.message);
     return {
       success: false,
       error: error.response?.data?.detail || "Error marcando como pagado",
       data: error.response?.data,
     };
   }
 };

export const confirmActivationPayment = async (username, action) => {
   try {
     const response = await api.post("/admin/activation/confirm", { username, action });
     return {
       success: true,
       data: response.data,
     };
   } catch (error) {
     console.error("❌ Error confirmando activación:", error.response?.data || error.message);
     return {
       success: false,
       error: error.response?.data?.detail || "Error confirmando la activación",
       data: error.response?.data,
     };
   }
 };

export const activateAccountManually = async (username, note = "") => {
   try {
     const response = await api.post("/admin/activation/manual", { username, note });
     return {
       success: true,
       data: response.data,
     };
   } catch (error) {
     console.error("❌ Error activando manualmente:", error.response?.data || error.message);
     return {
       success: false,
       error: error.response?.data?.detail || "Error activando manualmente",
       data: error.response?.data,
     };
   }
 };

export const updateCurrentUserPassword = async (newPassword) => {
  try {
    const response = await api.put("/me/password", {
      new_password: newPassword,
    });
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error actualizando contraseña:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error actualizando contraseña",
      data: error.response?.data,
    };
  }
};

export const getCurrentUser = async () => {
  try {
    const token = storage.get("token");
    if (!token) {
      return { success: false, error: "Token no disponible" };
    }
    const response = await api.get("/me");
    return { success: true, data: response.data };
  } catch (error) {
    console.error("❌ Error obteniendo usuario actual:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error obteniendo usuario",
      data: error.response?.data,
    };
  }
};

export const getAdminUserLog = async (email, lines = 200) => {
  try {
    const response = await api.get(`/admin/users/${encodeURIComponent(email)}/log`, {
      params: { lines },
    });
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error obteniendo log usuario:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error obteniendo log",
      data: error.response?.data,
    };
  }
};

export const getSymbolLiveLog = async (symbol) => {
  try {
    const response = await api.get(`/logs/symbol/${encodeURIComponent(symbol)}`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error obteniendo log del símbolo:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error obteniendo log del símbolo",
      data: error.response?.data,
    };
  }
};

// =====================================================
// 🔹 NOTICIAS
// =====================================================
export const getNewsState = async () => {
  try {
    const response = await api.get("/news/state");
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error obteniendo noticias:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error obteniendo noticias",
      data: error.response?.data,
    };
  }
};

// =====================================================
export const restartAdminUserBot = async (email) => {
  try {
    const response = await api.post(`/admin/users/${encodeURIComponent(email)}/restart-bot`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error reiniciando bot usuario:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error reiniciando bot",
      data: error.response?.data,
    };
  }
};

export const startAdminUserBot = async (email) => {
  try {
    const response = await api.post(`/admin/users/${encodeURIComponent(email)}/start-bot`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error iniciando bot usuario:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error iniciando bot",
      data: error.response?.data,
    };
  }
};

export const stopAdminUserBot = async (email) => {
  try {
    const response = await api.post(`/admin/users/${encodeURIComponent(email)}/stop-bot`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error deteniendo bot usuario:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error deteniendo bot",
      data: error.response?.data,
    };
  }
};

export const resendAdminUserAccess = async (email) => {
  try {
    const response = await api.post("/admin/users/resend", { email });
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error reenviando accesos:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error reenviando accesos",
      data: error.response?.data,
    };
  }
};

export const deleteAdminUser = async (email) => {
  try {
    const response = await api.delete(`/admin/users/${encodeURIComponent(email)}`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error eliminando usuario:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error eliminando usuario",
      data: error.response?.data,
    };
  }
};

export const logoutUser = async () => {
  try {
    const token = storage.get("token");
    
    if (!token) {
      // Si no hay token, solo limpiar storage
      clearTokenStorage();
      console.log("✅ Logout (sin token)");
      return {
        success: true,
        data: { message: "Sesión cerrada" },
      };
    }

    // ✅ LLAMAR AL ENDPOINT DE LOGOUT
    const response = await api.post("/logout", { token });
    
    // Limpiar storage
    clearTokenStorage();
    
    console.log("✅ Logout exitoso");
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error en logout:", error.response?.data || error.message);
    
    // Limpiar storage de todas formas
    clearTokenStorage();
    
    return {
      success: true,
      data: { message: "Sesión cerrada" },
    };
  }
};

export const verifyToken = async (token) => {
  try {
    const response = await api.post("/verify-token", { token });
    console.log("✅ Token verificado");
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error verificando token:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Token inválido",
    };
  }
};

export const refreshToken = async () => {
  try {
    const accessToken = await requestTokenRefresh();
    console.log("✅ Token renovado");
    return {
      success: true,
      data: { access_token: accessToken },
    };
  } catch (error) {
    console.error("❌ Error renovando token:", error.response?.data || error.message);
    clearTokenStorage();
    return {
      success: false,
      error: error.response?.data?.detail || "Error renovando token",
    };
  }
};

// =====================================================
// 🔹 FUNCIONES DE BALANCES Y ÓRDENES
// =====================================================

export const getBalances = async () => {
  try {
    const response = await api.get("/balances");
    //console.log("✅ Balances obtenidos:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error obteniendo balances:", error.response?.data || error.message);
    throw error;
  }
};

export const getActiveOrders = async () => {
  try {
    const response = await api.get("/orders/active", {
      params: { _ts: Date.now() },
      timeout: 5000,
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });
    //console.log(`✅ ${response.data?.length || 0} órdenes activas obtenidas`);
    //console.log("✅ Órdenes activas obtenidas");
    //console.log(response.data);
    return response.data || [];
  } catch (error) {
    console.error("❌ Error obteniendo órdenes activas:", error.response?.data || error.message);
    return [];
  }
};

export const sellActiveOrder = async (orderId) => {
  try {
    const clean = encodeURIComponent(String(orderId || "").trim());
    if (!clean) {
      return { success: false, error: "orderId inválido" };
    }
    const response = await api.post(`/orders/${clean}/sell`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error("❌ Error vendiendo orden:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error vendiendo orden",
      data: error.response?.data,
    };
  }
};

export const getOrderHistory = async (symbol, fromDate, toDate) => {
  try {
    const params = { _ts: Date.now() };
    const requestedSymbol = String(symbol || "").trim();
    if (requestedSymbol) {
      params.symbol = requestedSymbol;
    }
    if (fromDate) {
      params.from_date = fromDate;
    }
    if (toDate) {
      params.to_date = toDate;
    }
    const response = await api.get("/orders/history", { params });
    return response.data || [];
  } catch (error) {
    console.error("❌ Error obteniendo historial:", error.response?.data || error.message);
    return [];
  }
};

export const getBalanceReturns = async () => {
  try {
    const response = await api.get("/balance/returns");
    return response.data || null;
  } catch (error) {
    console.error("❌ Error obteniendo rendimiento:", error.response?.data || error.message);
    return null;
  }
};

export const getMarketSymbols = async () => {
  try {
    const response = await api.get("/market/symbols");
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error obteniendo símbolos:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error obteniendo símbolos",
      data: error.response?.data,
    };
  }
};

// =====================================================
// 🔹 FUNCIONES DE INDICADORES
// =====================================================

const indicatorsCache = new Map();
const indicatorsInFlight = new Map();
const indicatorsForbiddenUntil = new Map();
const INDICATORS_CACHE_TTL_MS = 8000;
const INDICATORS_FORBIDDEN_TTL_MS = 5 * 60 * 1000;
// Debe superar el peor caso del backend (HEAVY_ENDPOINT_TIMEOUT_SEC = 8 s), porque
// /indicators/{symbol} recalcula en frio cuando no hay cache. Con 6 s el front abortaba
// antes que el backend y el par caia en "sin indicadores disponibles todavia" aunque el
// backend respondiera bien. Margen amplio: el backend siempre cierra antes.
const INDICATORS_REQUEST_TIMEOUT_MS = 20000;

export const getMarketRegime = async (symbol) => {
  try {
    const response = await api.get(`/regime/${symbol}`);
    return response.data;
  } catch (error) {
    return { ok: false, data: { regimen: "moderado", factor: 1.0 } }
  }
};

export const getIndicators = async (symbol) => {
  const normalizedSymbol = String(symbol || "").trim().toUpperCase();
  try {
    if (!normalizedSymbol) {
      throw new Error("El símbolo es requerido");
    }

    const forbiddenUntil = indicatorsForbiddenUntil.get(normalizedSymbol) || 0;
    if (forbiddenUntil > Date.now()) {
      const error = new Error(`Indicadores no permitidos para ${normalizedSymbol}`);
      error.isForbiddenIndicator = true;
      throw error;
    }
    if (forbiddenUntil) {
      indicatorsForbiddenUntil.delete(normalizedSymbol);
    }

    const cached = indicatorsCache.get(normalizedSymbol);
    if (cached && Date.now() - cached.ts < INDICATORS_CACHE_TTL_MS) {
      return cached.data;
    }

    if (indicatorsInFlight.has(normalizedSymbol)) {
      return indicatorsInFlight.get(normalizedSymbol);
    }

    const requestPromise = (async () => {
      const response = await api.get(`/indicators/${normalizedSymbol}`, {
        timeout: INDICATORS_REQUEST_TIMEOUT_MS,
      });
      const payload = response?.data;

      if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        throw new Error("Formato inválido de indicadores");
      }

      if (payload.error) {
        // Se conserva codigo + estado_mercado: sin esto el panel solo puede decir
        // "sin indicadores disponibles" y descarta el motivo real del backend.
        const error = new Error(String(payload.error));
        error.codigoIndicadores = payload.codigo || null;
        error.estadoMercado = payload.estado_mercado || null;
        throw error;
      }

      // Normaliza strings numéricos a number para evitar errores con toFixed().
      const normalized = Object.fromEntries(
        Object.entries(payload).map(([key, value]) => {
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (trimmed !== "") {
              const asNumber = Number(trimmed);
              if (Number.isFinite(asNumber)) return [key, asNumber];
            }
          }
          return [key, value];
        })
      );

      indicatorsCache.set(normalizedSymbol, { ts: Date.now(), data: normalized });
      return normalized;
    })();

    indicatorsInFlight.set(normalizedSymbol, requestPromise);
    try {
      return await requestPromise;
    } finally {
      indicatorsInFlight.delete(normalizedSymbol);
    }
  } catch (error) {
    if (error?.response?.status === 403) {
      indicatorsForbiddenUntil.set(normalizedSymbol, Date.now() + INDICATORS_FORBIDDEN_TTL_MS);
      error.isForbiddenIndicator = true;
    }
    const backendDetail =
      error?.response?.data?.error ||
      error?.response?.data?.detail ||
      error?.message ||
      "Error obteniendo indicadores";
    if (!error?.isForbiddenIndicator) {
      console.error(`❌ Error obteniendo indicadores de ${normalizedSymbol || symbol}:`, backendDetail);
    }
    throw error;
  }
};

export const getDashboardResumen = async () => {
  try {
    const response = await api.get("/dashboard/resumen");
    //console.log("✅ Resumen del dashboard obtenido");
    return response.data;
  } catch (error) {
    console.error("❌ Error obteniendo resumen:", error.response?.data || error.message);
    return {
      ordenes_abiertas: 0,
      ordenes_hoy: 0,
      ganancia_total_hoy: 0,
      win_rate: "0%",
      ganancias: 0,
      perdidas: 0,
      balance_total_usdt: 0,
    };
  }
};

export const getTradingPairsStatus = async () => {
  try {
    const response = await api.get("/dashboard/pairs-status");
    return { success: true, data: response.data || {} };
  } catch (error) {
    console.error("❌ Error obteniendo estado de pares:", error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.detail || "Error obteniendo estado de pares",
      data: { pairs: [] },
    };
  }
};

export const getOrderflowStatus = async (symbol) => {
  try {
    const response = await api.get(`/orderflow/status/${encodeURIComponent(symbol)}`);
    return response.data || { ok: false, data: { gate_enabled: false, fresh_data: false } };
  } catch (error) {
    console.error("❌ Error obteniendo estado order flow:", error.response?.data || error.message);
    return {
      ok: false,
      error: error.response?.data?.detail || "Error obteniendo estado order flow",
      data: { gate_enabled: false, fresh_data: false },
    };
  }
};

export const getPrecioHistorico = async (symbol, limit = 100) => {
  try {
    if (!symbol) {
      throw new Error("El símbolo es requerido");
    }

    const response = await api.get(`/historico/precios/${symbol}`, {
      params: { limit },
    });
    //console.log(`✅ ${response.data?.length || 0} precios históricos obtenidos`);
    return response.data || [];
  } catch (error) {
    console.error(`❌ Error obteniendo histórico de ${symbol}:`, error.response?.data || error.message);
    return [];
  }
};

// =====================================================
// 🔹 FUNCIONES DE CONTROL DEL BOT
// =====================================================

export const startTrading = async () => {
  try {
    const response = await api.post("/trading/start");
    //console.log("✅ Trading iniciado");
    return response.data;
  } catch (error) {
    console.error("❌ Error iniciando trading:", error.response?.data || error.message);
    throw error;
  }
};

export const stopTrading = async () => {
  try {
    const response = await api.post("/trading/stop");
    //console.log("✅ Trading detenido");
    return response.data;
  } catch (error) {
    console.error("❌ Error deteniendo trading:", error.response?.data || error.message);
    throw error;
  }
};

export const activarCompras = async () => {
  try {
    const response = await api.post("/trading/activar_compras");
    //console.log("✅ Compras activadas");
    return response.data;
  } catch (error) {
    console.error("❌ Error activando compras:", error.response?.data || error.message);
    throw error;
  }
};

export const desactivarCompras = async () => {
  try {
    const response = await api.post("/trading/desactivar_compras");
    //console.log("✅ Compras desactivadas");
    return response.data;
  } catch (error) {
    console.error("❌ Error desactivando compras:", error.response?.data || error.message);
    throw error;
  }
};

// =====================================================
// ✅ EXPORTAR OBJETO apiService (para compatibilidad)
// =====================================================

export const apiService = {
  // Auth
  loginUser,
  registerUser,
  forgotPassword,
  createAdminUser,
  getAdminPreregistros,
  approvePreregistro,
  deletePreregistro,
  getAdminUsers,
  resendAdminUserAccess,
  deleteAdminUser,
  logoutUser,
  verifyToken,
  refreshToken,
  getCurrentUser,
  updateCurrentUserProfile,
  updateCurrentUserPassword,

  // Orden flow / profundidad de mercado
  getOrderflowStatus,

  // Balances y órdenes
  getBalances,
  getActiveOrders,
  getOrderHistory,
  getMarketSymbols,

  // Indicadores
  getIndicators,
  getDashboardResumen,
  getTradingPairsStatus,
  getPrecioHistorico,

  // Control del bot
  startTrading,
  stopTrading,
  activarCompras,
  desactivarCompras,
};

// ===== COMPARADOR MULTI-PAR =====

/**
 * Histórico multi-símbolo para comparar pares en un rango de tiempo.
 * @param {string[]} symbols  Lista de pares; vacío = todos los del usuario.
 * @param {number} horas      Ventana en horas (1, 4, 12, 24, 72, 168, 720).
 */
export const getPreciosMulti = async (symbols = [], horas = 24) => {
  try {
    const params = { horas };
    if (symbols && symbols.length) {
      params.symbols = symbols.join(",");
    }
    const response = await api.get("/historico/precios/multi", { params });
    return response.data;
  } catch (error) {
    console.error("❌ Error obteniendo histórico multi-par:", error.response?.data || error.message);
    throw error;
  }
};

/** Rangos de tiempo soportados por el comparador. */
export const getRangosHistorico = async () => {
  try {
    const response = await api.get("/historico/rangos");
    return response.data?.rangos || [];
  } catch (error) {
    console.error("❌ Error obteniendo rangos:", error.response?.data || error.message);
    return [];
  }
};

export default api;
